import React, { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ImCheckmark2 } from 'react-icons/im'
import PropTypes from 'prop-types'

import SkeletonListRow from '../../../../../features/components/List/Skeleton'
import PageSection from '../../../../../features/components/PageSection'
import List from '../../../../../features/components/List'
import ItemsLoading from '../../../../../components/Loaders/ItemsLoading'
import Chip from '../../../../../components/Chip'
import ActionsDropdown from '../../../../../features/components/ActionsDropdown'

import useGetMembersListActionOptions from './useGetMembersListActionOptions'

import { isTeamMemberInApprovers } from '../../helpers'

import {
  membersErrorSelector,
  membersIsLoadingSelector,
  membersSelector,
  membersWasLoadedSelector
} from '../../../../../modules/selectors/team'
import { selectedPartnershipRightsHolderSelfAccountSelector } from '../../../../../modules/selectors/app'

import { CHIP_COLORS } from '../../../../../constants/other'
import { listItemsTableColumnsMinWidth } from '../../../../../features/components/ListItemsTable/columnsWidth'

import useCommonStyles from '../../../../../styles/common/listPage'
import useStyles from './styles'

const MembersListTable = ({ selfAccountName, selfAccountId, openDeactivateUserModal }) => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()

  const { t } = useTranslation()

  const membersLists = useSelector(membersSelector)
  const selectedPartnershipRightsHolderSelfAccount = useSelector(selectedPartnershipRightsHolderSelfAccountSelector)

  const approvers = useMemo(
    () => selectedPartnershipRightsHolderSelfAccount?.approvers || [],
    [selectedPartnershipRightsHolderSelfAccount]
  )

  const { getMembersListActionDropdownOptions, memberUpdatingId } = useGetMembersListActionOptions({
    selfAccountName,
    selfAccountId,
    approvers,
    openDeactivateUserModal
  })

  const membersListColumns = useMemo(
    () => [
      {
        headTitle: '',
        Component: item => {
          const { full_name: fullName, user: userId } = item
          const isUserInApprovers = isTeamMemberInApprovers(approvers, userId)
          return (
            <div className={classes.memberName}>
              <span>{fullName}</span>
              {isUserInApprovers && <ImCheckmark2 className={classes.memberAdApproverTick} />}
            </div>
          )
        },
        style: {
          minWidth: 250
        }
      },
      {
        headTitle: 'Role',
        field: 'role'
      },
      {
        headTitle: '',
        Component: item => {
          const { is_active: isActive } = item

          return (
            <Chip
              text={isActive ? t('Active') : t('Deactivated')}
              color={isActive ? CHIP_COLORS.success : CHIP_COLORS.neutral}
            />
          )
        },
        style: {
          display: 'flex',
          justifyContent: 'flex-end'
        }
      },
      {
        Component: item => {
          if (!!getMembersListActionDropdownOptions(item).length) {
            return <ActionsDropdown closeOnInsideClick options={getMembersListActionDropdownOptions(item)} />
          } else {
            return null
          }
        },
        style: {
          minWidth: listItemsTableColumnsMinWidth.dropdown,
          maxWidth: listItemsTableColumnsMinWidth.dropdown,
          padding: 0
        }
      }
    ],
    [t, classes, getMembersListActionDropdownOptions, approvers]
  )

  const skeletonProps = useMemo(() => ({ cols: membersListColumns }), [membersListColumns])

  return (
    <ItemsLoading
      className={commonClasses.itemList}
      itemsLength={membersLists.length}
      SkeletonComponent={SkeletonListRow}
      isLoadingSelector={membersIsLoadingSelector}
      wasLoadedSelector={membersWasLoadedSelector}
      errorSelector={membersErrorSelector}
      skeletonProps={{ cols: membersListColumns }}
      ContentWrapper={PageSection}
      isEmptyNoDataMessage
    >
      <List
        cols={membersListColumns}
        listData={membersLists}
        itemUpdatingId={memberUpdatingId}
        Skeleton={<SkeletonListRow {...skeletonProps} key={uuidv4()} />}
      />
    </ItemsLoading>
  )
}

MembersListTable.propTypes = {
  selfAccountName: PropTypes.string.isRequired,
  selfAccountId: PropTypes.number.isRequired,
  openDeactivateUserModal: PropTypes.func.isRequired
}

export default MembersListTable
